/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {defaultsDeep, isObject, kebabCase} from 'lodash';
import {Swiper as BaseSwiper, SwiperSlide as BaseSwiperSlide} from 'swiper/react';

import {chakra} from '@chakra-ui/react';

import {Box, Button, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import {applySwiperThumbTabsA11y, isSwiperThumbVariant} from '@amblique/king-living/app/hooks/use-swiper-thumb-tabs-a11y';
import {swiperModules} from '@amblique/base/app/constants/options/swiper-modules';
import {SWIPER_MODULE_DEFAULTS, SWIPER_OPTIONS} from '@amblique/king-living/app/constants/swiper-options';
import {usePluginOptions} from '@amblique/base/app/hooks/use-plugin-options';
import {
  CarouselArrowPrevIcon,
  CarouselArrowNextIcon,
  GalleryPauseIcon,
  GalleryPlayIcon,
} from '@amblique/king-living/app/components/icons';

const ChakraSwiper = chakra(BaseSwiper);
const ChakraSlide = chakra(BaseSwiperSlide);

ChakraSwiper.displayName = BaseSwiper.displayName;
ChakraSlide.displayName = BaseSwiperSlide.displayName;

const CF012_NAV_ICON_DIM = {
  display: 'block',
  height: '20px',
  width: '10px',
};

/** CF-012: Figma chevrons — `currentColor` + `.swiper-button-disabled` opacity in theme */
function Cf012NavPrevIcon() {
  return (
    <Box
      as="svg"
      fill="none"
      viewBox="0 0 11 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...CF012_NAV_ICON_DIM}
    >
      <path
        clipRule="evenodd"
        d="M10.5448 19.774C10.4733 19.8457 10.3885 19.9025 10.295 19.9413C10.2016 19.98 10.1014 20 10.0002 20C9.89907 20 9.79889 19.98 9.70545 19.9413C9.61201 19.9025 9.52713 19.8457 9.45569 19.774L0.226001 10.5443C0.154374 10.4729 0.0975455 10.388 0.058771 10.2946C0.0199965 10.2011 3.80587e-05 10.101 3.80675e-05 9.9998C3.80764e-05 9.89863 0.0199966 9.79845 0.0587711 9.70501C0.0975456 9.61157 0.154374 9.52669 0.226001 9.45525L9.45569 0.225561C9.60011 0.0811366 9.79599 -8.52164e-08 10.0002 -6.73606e-08C10.2045 -4.95048e-08 10.4004 0.0811367 10.5448 0.225561C10.6892 0.369984 10.7704 0.565865 10.7704 0.770111C10.7704 0.974358 10.6892 1.17024 10.5448 1.31466L1.85812 9.9998L10.5448 18.6849C10.6164 18.7564 10.6732 18.8413 10.712 18.9347C10.7508 19.0281 10.7708 19.1283 10.7708 19.2295C10.7708 19.3307 10.7508 19.4308 10.712 19.5243C10.6732 19.6177 10.6164 19.7026 10.5448 19.774Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </Box>
  );
}

function Cf012NavNextIcon() {
  return (
    <Box
      as="svg"
      fill="none"
      viewBox="0 0 11 20"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      {...CF012_NAV_ICON_DIM}
    >
      <path
        clipRule="evenodd"
        d="M0.225963 0.225964C0.29741 0.154337 0.382286 0.0975082 0.475729 0.0587336C0.569172 0.019959 0.669347 0 0.770515 0C0.871684 0 0.971859 0.019959 1.0653 0.0587336C1.15874 0.0975082 1.24362 0.154337 1.31507 0.225964L10.5448 9.45565C10.6164 9.5271 10.6732 9.61197 10.712 9.70542C10.7508 9.79886 10.7707 9.89903 10.7707 10.0002C10.7707 10.1014 10.7508 10.2015 10.712 10.295C10.6732 10.3884 10.6164 10.4733 10.5448 10.5448L1.31507 19.7744C1.17064 19.9189 0.974762 20 0.770515 20C0.566269 20 0.370388 19.9189 0.225963 19.7744C0.0815395 19.63 0.000402697 19.4341 0.000402697 19.2299C0.000402697 19.0256 0.0815395 18.8298 0.225963 18.6853L8.91264 10.0002L0.225963 1.31507C0.154336 1.24362 0.0975074 1.15875 0.0587328 1.0653C0.0199582 0.971859 0 0.871684 0 0.770516C0 0.669347 0.0199582 0.569172 0.0587328 0.475729C0.0975074 0.382286 0.154336 0.297411 0.225963 0.225964Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </Box>
  );
}

/**
 * @param {{
 *  children: React.ReactNode,
 *  fallback: React.ReactNode,
 *  className: string,
 *  options: string | object,
 *  presets: string | string[],
 * }} args
 *
 * @returns {React.ReactElement}
 */
export const Swiper = ({
  children,
  fallback,
  className = '',
  variant = '',
  options,
  presets = '',
  slideProps = {},
  onSwiper: onSwiperProp,
  snapLastSlide = false,
  onThumbActivate,
  thumbActivateOnClick = false,
  thumbTabListLabel = 'Carousel thumbnails',
  ...props
}) => {
  const swiperRef = useRef(null);
  const swiperContainerRef = useRef(null);
  const thumbA11yCleanupRef = useRef(null);
  const onThumbActivateRef = useRef(onThumbActivate);

  useEffect(() => {
    onThumbActivateRef.current = onThumbActivate;
  }, [onThumbActivate]);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  const [cf010DescOffsetPx, setCf010DescOffsetPx] = useState(0);

  const measureCf050BlogCoverHeight = useCallback(() => {
    if (variant !== 'blog-related-articles') return;

    const root = swiperContainerRef.current ?? swiperRef.current?.el;

    if (!root || typeof window === 'undefined') return;

    const slide =
      root.querySelector('.swiper-slide-active') ??
      root.querySelector('.swiper-slide-visible') ??
      root.querySelector('.swiper-slide');
    const cover = slide?.querySelector('[data-blog-summary-cover]');

    if (!cover) {
      root.style.removeProperty('--cf050-cover-h');

      return;
    }

    const h = cover.getBoundingClientRect().height;
    const containerWidth = root.clientWidth;
    // Cover uses aspect-ratio up to 1:1; reject runaway measurements from unconstrained flex/grid loops.
    const maxReasonableHeight =
      containerWidth > 0 ? Math.max(containerWidth, containerWidth * (67 / 60)) : 600;

    if (Number.isFinite(h) && h > 0 && h <= maxReasonableHeight) {
      root.style.setProperty('--cf050-cover-h', `${Math.round(h * 100) / 100}px`);
    } else {
      root.style.removeProperty('--cf050-cover-h');
    }
  }, [variant]);

  const handleCf050BlogCoverSlideChange = useCallback(() => {
    requestAnimationFrame(() => measureCf050BlogCoverHeight());
  }, [measureCf050BlogCoverHeight]);

  presets = useMemo(() => (Array.isArray(presets) ? presets : presets?.split(/[\n\s,]+/) || []), [presets]);
  const isThumbStrip = isSwiperThumbVariant(variant, presets);
  className = [...new Set(presets.map((preset) => `swiper-${kebabCase(preset)}`).concat(className.split(/[\n\s,]+/)))].join(' ');

  // Post-process callback: Handle module defaults
  // Note: resolvedOptions now includes responsive overrides already applied
  const postProcess = useCallback((resolvedOptions) => {
    for (const [moduleName, moduleDefaults] of Object.entries(SWIPER_MODULE_DEFAULTS)) {
      if (resolvedOptions[moduleName] === true) {
        resolvedOptions[moduleName] = {...moduleDefaults};
      } else if (isObject(resolvedOptions[moduleName])) {
        defaultsDeep(resolvedOptions[moduleName], moduleDefaults);
      }
    }

    // Auto correct the slidesPerGroup based on slidesPerView
    if (resolvedOptions.slidesPerGroup > resolvedOptions.slidesPerView) {
      resolvedOptions.slidesPerGroup = resolvedOptions.slidesPerView;
    }

    return resolvedOptions;
  }, []);

  // Use generic plugin options hook (now handles responsive options internally)
  const resolvedOptions = usePluginOptions('swiper', SWIPER_OPTIONS, presets, options, postProcess);
  const styles = useMultiStyleConfig('Swiper', {presets, options: resolvedOptions, variant});
  const {navigation, pagination, scrollbar} = resolvedOptions;

  const measureCf010DescOffset = useCallback(() => {
    if (variant !== 'CF-010') return;

    const root = swiperContainerRef.current ?? swiperRef.current?.el;

    if (!root || typeof window === 'undefined') return;

    const slide = root.querySelector('.swiper-slide-active') ?? root.querySelector('.swiper-slide');

    if (!slide) {
      setCf010DescOffsetPx(0);

      return;
    }

    const host = slide.querySelector('.component--image');

    if (!host) {
      setCf010DescOffsetPx(0);

      return;
    }

    const img = host.querySelector('img');

    if (!img) {
      const desc = host.querySelector('.rich-text-content');

      if (!desc) {
        setCf010DescOffsetPx(0);

        return;
      }

      const h = desc.getBoundingClientRect().height;

      setCf010DescOffsetPx(Number.isFinite(h) && h > 0 ? Math.round(h * 100) / 100 : 0);

      return;
    }

    const hostRect = host.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const belowImage = hostRect.bottom - imgRect.bottom;

    setCf010DescOffsetPx(
      Number.isFinite(belowImage) && belowImage > 0 ? Math.round(belowImage * 100) / 100 : 0
    );
  }, [variant]);

  const handleCf010SlideChange = useCallback(() => {
    requestAnimationFrame(() => measureCf010DescOffset());
  }, [measureCf010DescOffset]);

  useLayoutEffect(() => {
    if (variant !== 'CF-010' || (!navigation && !pagination)) return undefined;

    measureCf010DescOffset();

    const root = swiperContainerRef.current ?? swiperRef.current?.el;

    if (!root) return undefined;

    const ro = new ResizeObserver(() => measureCf010DescOffset());

    ro.observe(root);

    const host = root.querySelector('.swiper-slide-active .component--image');

    if (host) ro.observe(host);

    window.addEventListener('resize', measureCf010DescOffset);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureCf010DescOffset);
    };
  }, [variant, navigation, pagination, measureCf010DescOffset, children?.length]);

  useEffect(() => {
    if (variant !== 'CF-010') {
      setCf010DescOffsetPx(0);
    }
  }, [variant]);

  useEffect(() => () => {
    thumbA11yCleanupRef.current?.();
    thumbA11yCleanupRef.current = null;
    swiperRef.current?.off?.('slideChange', handleCf010SlideChange);
  }, [handleCf010SlideChange]);

  useLayoutEffect(() => {
    if (variant !== 'blog-related-articles' || (!navigation && !pagination)) return undefined;

    measureCf050BlogCoverHeight();

    const root = swiperContainerRef.current ?? swiperRef.current?.el;

    if (!root) return undefined;

    const ro = new ResizeObserver(() => measureCf050BlogCoverHeight());

    root.querySelectorAll('[data-blog-summary-cover]').forEach((el) => {
      ro.observe(el);
    });

    window.addEventListener('resize', measureCf050BlogCoverHeight);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureCf050BlogCoverHeight);
      root.style.removeProperty('--cf050-cover-h');
    };
  }, [variant, navigation, pagination, measureCf050BlogCoverHeight, children?.length]);

  useEffect(() => {
    if (variant !== 'blog-related-articles') {
      const root = swiperContainerRef.current ?? swiperRef.current?.el;

      root?.style?.removeProperty?.('--cf050-cover-h');
    }
  }, [variant]);

  useEffect(() => () => {
    swiperRef.current?.off?.('slideChange', handleCf050BlogCoverSlideChange);
  }, [handleCf050BlogCoverSlideChange]);

  const {sx: sxProp, ...restProps} = props;
  const {sx: themeContainerSx, ...restContainer} = styles.container || {};

  const containerSx = useMemo(() => ({
    ...(themeContainerSx && typeof themeContainerSx === 'object' ? themeContainerSx : {}),
    ...(variant === 'CF-010' && (navigation || pagination) ? {
      '--swiper-cf010-desc-offset': `${cf010DescOffsetPx}px`,
    } : {}),
    ...(sxProp && typeof sxProp === 'object' ? sxProp : {}),
  }), [themeContainerSx, variant, navigation, pagination, cf010DescOffsetPx, sxProp]);

  const onSwiper = useCallback((swiper) => {
    swiperRef.current = swiper;
    onSwiperProp?.(swiper);
    setAutoplayPaused(false);

    if (isThumbStrip) {
      thumbA11yCleanupRef.current?.();
      thumbA11yCleanupRef.current = applySwiperThumbTabsA11y(swiper, {
        label: thumbTabListLabel,
        onActivate: (index) => onThumbActivateRef.current?.(index),
        activateOnClick: thumbActivateOnClick,
      });
    }

    // Only re-measure when the preset opts into auto-height. `updateAutoHeight()` forces the
    // wrapper to inline `height: <active-slide-height>px` regardless of the `autoHeight` flag,
    // so calling it for fixed-height presets (e.g. `range_tile`, image carousels inside an
    // AspectRatio container) collapses the wrapper to 0 whenever the active slide's image
    // hasn't finished loading by the 500ms mark — Cylindo's first cold render comfortably
    // exceeds 500ms, which is why those tiles only "fixed themselves" after PDP→back warmed
    // the CDN. KINDW-99 PLP-03.
    if (resolvedOptions.autoHeight) {
      setTimeout(() => {
        swiper.updateAutoHeight();
      }, 500);
    }

    if (variant === 'CF-010') {
      swiper.off('slideChange', handleCf010SlideChange);
      swiper.on('slideChange', handleCf010SlideChange);
      requestAnimationFrame(() => measureCf010DescOffset());
    }

    if (variant === 'blog-related-articles') {
      swiper.off('slideChange', handleCf050BlogCoverSlideChange);
      swiper.on('slideChange', handleCf050BlogCoverSlideChange);
      requestAnimationFrame(() => measureCf050BlogCoverHeight());
      setTimeout(() => measureCf050BlogCoverHeight(), 100);
      setTimeout(() => measureCf050BlogCoverHeight(), 520);
    }

    if (snapLastSlide) {
      const checkAndSnapLastSlide = (swiperInstance) => {
        const {slides, el} = swiperInstance;

        if (!slides?.length || !el) return;

        // Only snap when moving forward (Next direction), not when going backward (Prev)
        const isMovingForward =
          swiperInstance.previousIndex === undefined ||
          swiperInstance.activeIndex >= swiperInstance.previousIndex;

        if (!isMovingForward) return;

        const lastSlide = slides[slides.length - 1];
        const containerRect = el.getBoundingClientRect();
        const lastSlideRect = lastSlide.getBoundingClientRect();
        // Use inner right (exclude padding) — slides inside padding zone are visually clipped
        const paddingRight = parseFloat(window.getComputedStyle(el).paddingRight) || 0;
        const containerInnerRight = containerRect.right - paddingRight;
        const overflow = lastSlideRect.right - containerInnerRight;
        const threshold = lastSlide.offsetWidth / 5;

        if (overflow > 0 && overflow < threshold) {
          const currentTranslate = swiperInstance.getTranslate();
          const maxTranslate = swiperInstance.maxTranslate();

          if (Math.abs(currentTranslate - maxTranslate) <= 2) return;

          swiper.slideTo(slides.length - 1, 300);
        }
      };

      swiper.on('slideChange', () => checkAndSnapLastSlide(swiper));
      swiper.on('slideChangeTransitionEnd', () => checkAndSnapLastSlide(swiper));
    }
  }, [
    onSwiperProp,
    isThumbStrip,
    thumbActivateOnClick,
    thumbTabListLabel,
    variant,
    snapLastSlide,
    resolvedOptions.autoHeight,
    handleCf010SlideChange,
    measureCf010DescOffset,
    handleCf050BlogCoverSlideChange,
    measureCf050BlogCoverHeight
  ]);

  const toggleAutoplay = useCallback(() => {
    const swiper = swiperRef.current;
    const ap = swiper?.autoplay;

    if (!swiper || !ap) return;

    // Use stop/start (not pause/resume): pause()/running+paused flags are unreliable
    // and can leave the autoplay timer advancing (KINDW-1181 / CF-010).
    if (autoplayPaused) {
      ap.start();
      setAutoplayPaused(false);
    } else {
      ap.stop();
      setAutoplayPaused(true);
    }
  }, [autoplayPaused]);

  return children?.length > 0 ? (
    <ChakraSwiper
      ref={swiperContainerRef}
      className={className}
      modules={swiperModules}
      onSwiper={onSwiper}
      {...resolvedOptions}
      {...restContainer}
      sx={containerSx}
      {...restProps}
    >
      {(navigation || pagination) ? (
        <Box
          className="swiper-navigation-pagination"
          slot="container-start"
          {...styles.paginationContainer}
        >
          {navigation && (
            <Box className="swiper-button-prev" {...styles.navigation}>
              {variant === 'CF-010' && <CarouselArrowPrevIcon boxSize="10px" color="white" height="10px" width="10px" />}
              {(variant === 'CF-012' || variant === 'blog-related-articles') && <Cf012NavPrevIcon />}
            </Box>
          )}
          {pagination && <Box className="swiper-pagination" {...styles.pagination} />}
          {navigation && (
            <Box className="swiper-button-next" {...styles.navigation}>
              {variant === 'CF-010' && <CarouselArrowNextIcon boxSize="10px" color="white" height="10px" width="10px" />}
              {(variant === 'CF-012' || variant === 'blog-related-articles') && <Cf012NavNextIcon />}
            </Box>
          )}
        </Box>
      ) : null}

      {children.map((child, index) => (
        <ChakraSlide key={child.key || index}
          {...styles.slide}
          {...slideProps}
        >
          {child}
        </ChakraSlide>
      ))}

      {resolvedOptions.autoplay && (
        <Button className="swiper-autoplay" variant="link" onClick={toggleAutoplay} {...styles.autoplay}>
          {autoplayPaused ? <GalleryPlayIcon /> : <GalleryPauseIcon />}
        </Button>
      )}

      {scrollbar && <Box className="swiper-scrollbar" {...styles.scrollbar} />}
    </ChakraSwiper>
  ) : (typeof fallback === 'function' ? fallback() : fallback);
};

Swiper.displayName = 'Swiper';
Swiper.propTypes = {
  ...BaseSwiper.propTypes,
  children: PropTypes.node,
  fallback: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  className: PropTypes.string,
  variant: PropTypes.string,
  presets: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  options: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  slideProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  snapLastSlide: PropTypes.bool,
  onThumbActivate: PropTypes.func,
  thumbActivateOnClick: PropTypes.bool,
  thumbTabListLabel: PropTypes.string,
};
